import { useCallback, useEffect, useRef, useState } from "react";

import {
  ACTIVITY_KEY, TIMEOUT_MS, awayAtStripe, clearAwayAtStripe, idleState, lastActivity, markActivity,
} from "../util/idle";

// What counts as activity. Scroll events don't bubble, hence the capture phase.
const EVENTS = ["pointerdown", "pointermove", "keydown", "wheel", "touchstart", "scroll"];
const WRITE_EVERY_MS = 5000; // the activity is written at most this often
const TICK_MS = 1000;

// While `active`: records the activity (shared/util/idle.js), and says when
// to warn and when the time is up (onExpire). While the warning shows,
// activity doesn't count: only stay() keeps the session.
// Returns { warning, remaining (ms until the logout), stay }.
export function useIdleTimer({ active, onExpire }) {
  const [state, setState] = useState({ phase: "active", remaining: TIMEOUT_MS });
  const phase = useRef("active");
  const expire = useRef(onExpire);
  const expired = useRef(false); // onExpire once per idle period

  useEffect(() => {
    expire.current = onExpire;
  }, [onExpire]);

  const tick = useCallback(() => {
    const now = Date.now();
    // another tab is paying at Stripe: nobody is idle
    const next = awayAtStripe(now) ? { phase: "active", remaining: TIMEOUT_MS } : idleState(now);
    phase.current = next.phase;
    if (next.phase === "expired") {
      if (!expired.current) {
        expired.current = true;
        expire.current();
      }
      return;
    }
    expired.current = false;
    // a new state only when the shown second changes
    setState((prev) => (prev.phase === next.phase && Math.ceil(prev.remaining / 1000) === Math.ceil(next.remaining / 1000)
      ? prev : next));
  }, []);

  const stay = useCallback(() => {
    markActivity();
    tick();
  }, [tick]);

  useEffect(() => {
    if (!active) return undefined;
    if (lastActivity() == null) markActivity();
    let written = 0;
    const onActivity = () => {
      if (phase.current !== "active") return;
      const now = Date.now();
      if (now - written < WRITE_EVERY_MS) return;
      written = now;
      markActivity(now);
    };
    // another tab's activity or "Stay logged in"
    const onStorage = (event) => {
      if (event.key === ACTIVITY_KEY) tick();
    };
    const onVisible = () => {
      if (document.visibilityState === "visible") tick();
    };
    // back from Stripe through the browser's back button (page restored
    // from the cache, so App.js doesn't start again)
    const onPageShow = (event) => {
      if (!event.persisted) return;
      clearAwayAtStripe();
      markActivity();
      tick();
    };
    EVENTS.forEach((name) => window.addEventListener(name, onActivity, { capture: true, passive: true }));
    window.addEventListener("storage", onStorage);
    window.addEventListener("pageshow", onPageShow);
    document.addEventListener("visibilitychange", onVisible);
    const timer = setInterval(tick, TICK_MS);
    tick();
    return () => {
      EVENTS.forEach((name) => window.removeEventListener(name, onActivity, { capture: true }));
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("pageshow", onPageShow);
      document.removeEventListener("visibilitychange", onVisible);
      clearInterval(timer);
      phase.current = "active";
    };
  }, [active, tick]);

  return { warning: active && state.phase === "warning", remaining: state.remaining, stay };
}
